import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types/auth';
import { authService } from '../services/auth';
import { entraAuthService } from '../services/entraAuth';
import { AuthType } from '../types/entraAuth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
  isAdmin: () => boolean;
  canUploadDocuments: () => boolean;
  canManageUsers: () => boolean;
  // Entra additions (DOA-aligned v2.2)
  checkDomain: (email: string) => Promise<AuthType>;
  loginEntraInternal: () => void;
  loginEntraExternal: (email: string) => void;
  finalizeFromCode: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const initAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      setLoading(false);
      return;
    }

    // Hydrate from cache synchronously and unblock render. The 24h JWT
    // means the cached user is almost always still valid; in the rare
    // case the server rejects the token, the api.ts response interceptor
    // will catch the 401 on the next protected request and bounce to
    // /login. Net effect: one fewer RTT on every warm load.
    const savedUser = localStorage.getItem('user');
    let renderedFromCache = false;
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        renderedFromCache = true;
      } catch {
        // Cached user JSON is corrupt — fall through and treat as
        // a cold path: spinner stays visible until the network call
        // resolves or fails.
      }
    }

    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      if (!renderedFromCache) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]); // Add initAuth to useEffect dependencies

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
      console.log('Logged out successfully');
    }
  };

  const isAdmin = () => {
    if (!user) return false;
    return user.is_admin || user.role === 'admin';
  };

  const canUploadDocuments = () => {
    // Only full admins upload documents (Phase 9b: USER_ADMIN cannot).
    return isAdmin();
  };

  const canManageUsers = () => {
    if (!user) return false;
    return isAdmin() || user.role === 'user_admin';
  };

  // --- Entra auth methods (v2.2) ---------------------------------------

  const checkDomain = async (email: string): Promise<AuthType> => {
    const resp = await entraAuthService.checkDomain(email);
    return resp.authType;
  };

  const loginEntraInternal = (): void => {
    entraAuthService.loginInternal();
  };

  const loginEntraExternal = (email: string): void => {
    entraAuthService.loginExternal(email);
  };

  const finalizeFromCode = async (code: string): Promise<void> => {
    const resp = await entraAuthService.validateCode(code);
    localStorage.setItem('access_token', resp.access_token);
    // Reshape the Entra user into the broader User interface so the rest
    // of the app doesn't need to know which path minted the token.
    const hydrated: User = {
      id: resp.user.id,
      username: resp.user.username,
      email: resp.user.email,
      full_name: resp.user.full_name,
      is_active: true,
      is_admin: resp.user.is_admin,
      role: resp.user.role,
      auth_source: resp.user.auth_source,
      created_at: new Date().toISOString(),
      last_login: new Date().toISOString(),
    };
    localStorage.setItem('user', JSON.stringify(hydrated));
    setUser(hydrated);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      logout,
      isAdmin,
      canUploadDocuments,
      canManageUsers,
      checkDomain,
      loginEntraInternal,
      loginEntraExternal,
      finalizeFromCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
